import dayjs from 'dayjs';
import {
  PROMOTION_SCHEDULE_STATUS,
  PROMOTION_SCHEDULE_TRACK,
  PROMOTION_SCHEDULE_TYPE,
  PromotionScheduleListFilter,
  PromotionScheduleStatus,
  PromotionScheduleTrack,
  PromotionScheduleType,
} from '../../../../lib/api/promotionSchedule/types';
import { DATE_FORMAT } from '../../../../types/common/date';

/**
 * 프로모션 리스트 URL 쿼리 ↔ 필터 변환 (AMZUWEB-682).
 *
 * 훅에서 떼어 **순수 함수만** 둔 이유가 둘 있다.
 * ① 이 화면에서 가장 깨지기 쉬운 곳이라 단위 테스트를 붙여야 하는데,
 *    훅 파일은 공통 리스트 모듈 배럴을 끌어와 테스트가 앱 전체 모듈 그래프를 로드하게 된다.
 * ② 파싱 규칙은 화면이 아니라 **URL 계약**에 속한다 — 상세·타임라인에서 링크를 만들 때도 쓸 수 있다.
 *
 * ⚠️ 타입을 배럴(`lib/api/promotionSchedule`)이 아니라 `.../types` 에서 직접 가져온다 —
 * 배럴은 API 클라이언트까지 실어 오고, 그게 `superFetchV2` → redux store 로 이어져
 * **테스트가 앱 전체 모듈 그래프를 로드하다 죽는다.** `types` 는 런타임 의존이 없는 잎이다.
 */

export const EMPTY_SCHEDULE_LIST_FILTER: PromotionScheduleListFilter = {};

const TRACK_VALUES = Object.values(PROMOTION_SCHEDULE_TRACK) as string[];
const STATUS_VALUES = Object.values(PROMOTION_SCHEDULE_STATUS) as string[];
const TYPE_VALUES = Object.values(PROMOTION_SCHEDULE_TYPE) as string[];

/**
 * 콤마 목록에서 **아는 값만** 남긴다.
 *
 * 주소창을 손댄 링크에서 `tracks=PROMOTION,HACK` 같은 게 들어와도 조회는 되게 한다 —
 * 던지면 화면이 비고, 통째로 버리면 사용자가 고른 조건까지 사라진다.
 */
const parseEnumList = <T extends string>(raw: string | null, allowed: string[]): T[] | undefined => {
  if (!raw) {
    return undefined;
  }

  const values = raw
    .split(',')
    .map((item) => item.trim())
    .filter((item) => allowed.includes(item)) as T[];

  return values.length > 0 ? values : undefined;
};

/** 담당자 id — 0 은 "미지정" 센티널이라 허용한다 (음수·소수만 버린다) */
const parseAdminIds = (raw: string | null): number[] | undefined => {
  if (!raw) {
    return undefined;
  }

  const values = raw
    .split(',')
    .map((item) => Number(item.trim()))
    .filter((value) => Number.isInteger(value) && value >= 0);

  return values.length > 0 ? values : undefined;
};

const parseBoolean = (raw: string | null): boolean | undefined => {
  if (raw === 'true') return true;
  if (raw === 'false') return false;
  return undefined;
};

/** 날짜는 형식뿐 아니라 **달력 유효성**까지 본다 — `2026-02-31` 은 서버에서 400 이다 */
const parseDate = (raw: string | null): string | undefined => {
  if (!raw) {
    return undefined;
  }

  return dayjs(raw, DATE_FORMAT.YMD_DASH_FORMAT, true).isValid() ? raw : undefined;
};

export const parseScheduleFilter = (params: URLSearchParams): PromotionScheduleListFilter => {
  const keyword = params.get('keyword')?.trim();

  return {
    from: parseDate(params.get('from')),
    to: parseDate(params.get('to')),
    tracks: parseEnumList<PromotionScheduleTrack>(params.get('tracks'), TRACK_VALUES),
    statuses: parseEnumList<PromotionScheduleStatus>(params.get('statuses'), STATUS_VALUES),
    types: parseEnumList<PromotionScheduleType>(params.get('types'), TYPE_VALUES),
    adminIds: parseAdminIds(params.get('adminIds')),
    hasEvent: parseBoolean(params.get('hasEvent')),
    hasCoupon: parseBoolean(params.get('hasCoupon')),
    keyword: keyword || undefined,
  };
};

export const serializeScheduleFilter = (filter: PromotionScheduleListFilter): Record<string, string> => {
  const params: Record<string, string> = {};

  if (filter.from) params.from = filter.from;
  if (filter.to) params.to = filter.to;
  if (filter.tracks?.length) params.tracks = filter.tracks.join(',');
  if (filter.statuses?.length) params.statuses = filter.statuses.join(',');
  if (filter.types?.length) params.types = filter.types.join(',');
  if (filter.adminIds?.length) params.adminIds = filter.adminIds.join(',');
  // 연결 여부는 `false` 가 **의미 있는 값**이다 — 빈 값 체크로 거르면 안 된다
  if (filter.hasEvent !== undefined) params.hasEvent = String(filter.hasEvent);
  if (filter.hasCoupon !== undefined) params.hasCoupon = String(filter.hasCoupon);
  if (filter.keyword) params.keyword = filter.keyword;

  return params;
};
